import React from 'react';
import type { CrosscheckCounts, ScanResult } from '@privisy/api-types';
import type { CrosscheckVariant } from './CrosscheckExplainer';
import { CrosscheckExplainerProvider, useOpenCrosscheckExplainer } from './crosscheck/ExplainerContext';

interface CrosscheckBannerProps {
  /** Undefined or all zeros: the report has no displayed verdict, so no banner. */
  counts?: CrosscheckCounts;
  /**
   * `summary` is the shared #/sales view, which carries counts but no cards,
   * so its copy points at the full audit instead of "the card".
   */
  variant?: CrosscheckVariant;
  /**
   * The report's `crosscheckStatus` (#1960): an anonymous audit's crosscheck
   * runs after unlock, so the unlocked report says it is on its way (or that
   * it could not run) until the verdicts land.
   */
  status?: ScanResult['crosscheckStatus'];
}

function ShieldCheckIcon() {
  return (
    <svg
      className="crosscheck-banner__icon"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

/**
 * Introduces AI Crosscheck (the stage-5 policy judge) once, above section 01
 * (#1658), so each card's crosscheck reading has context. Violet, never green:
 * green means passing elsewhere on the page, and a confirmed crosscheck on a
 * failing check is not a pass. Takes counts rather than a ScanResult so the
 * owner's report and the shared sales view render the same banner.
 */
export default function CrosscheckBanner(props: CrosscheckBannerProps) {
  // On the report the page's provider owns the dialog, so the banner and the
  // disputed cards (#1659) open the same one. Standalone (the #/sales view),
  // the banner brings its own.
  const open = useOpenCrosscheckExplainer();
  if (open) return <BannerBody {...props} />;
  return (
    <CrosscheckExplainerProvider counts={props.counts} variant={props.variant}>
      <BannerBody {...props} />
    </CrosscheckExplainerProvider>
  );
}

function BannerBody({ counts, variant = 'report', status }: CrosscheckBannerProps) {
  const open = useOpenCrosscheckExplainer();

  if (status) {
    return (
      <section className="crosscheck-banner" aria-label="AI Crosscheck">
        <ShieldCheckIcon />
        <div className="crosscheck-banner__text">
          {status === 'pending' ? (
            <>
              <h2 className="crosscheck-banner__title">AI Crosscheck pending</h2>
              <p className="crosscheck-banner__body">
                A second read of your privacy policy is running. Its readings appear on the cards when you reload this
                report in a few minutes. They add context and don&apos;t change the score.
              </p>
            </>
          ) : (
            <>
              <h2 className="crosscheck-banner__title">AI Crosscheck unavailable for this report</h2>
              <p className="crosscheck-banner__body">
                The policy text from this scan is no longer stored, so AI Crosscheck can&apos;t give it a second read. The
                pattern checks below are complete.
              </p>
            </>
          )}
        </div>
      </section>
    );
  }

  if (!counts || counts.confirmed + counts.disputed + counts.corrected === 0) return null;

  return (
    <section className="crosscheck-banner" aria-label="AI Crosscheck">
      <ShieldCheckIcon />
      <div className="crosscheck-banner__text">
        {/* Scoped to what the judge guarantees (#1658 review): only a "the
            disclosure is there" reading needs a verified quote, and skipped or
            unverified checks get no second read at all. */}
        <h2 className="crosscheck-banner__title">AI Crosscheck: privacy-policy findings get a second read</h2>
        <p className="crosscheck-banner__body">
          A pattern check scans your policy, then AI Crosscheck reads it on its own. When it says a disclosure is
          there, it has to quote it word for word.{' '}
          {variant === 'summary'
            ? 'The full audit shows both readings and the quote for each disputed finding.'
            : 'When the two disagree, the card shows both readings.'}{' '}
          <button type="button" className="crosscheck-banner__link no-print" onClick={() => open?.()}>
            How AI Crosscheck works
          </button>
        </p>
      </div>
      <ul className="crosscheck-banner__pills">
        <li className="crosscheck-pill crosscheck-pill--confirmed">{counts.confirmed} confirmed</li>
        <li className="crosscheck-pill crosscheck-pill--disputed">{counts.disputed} disputed</li>
        {counts.corrected > 0 && (
          <li className="crosscheck-pill crosscheck-pill--disputed">{counts.corrected} corrected</li>
        )}
      </ul>
    </section>
  );
}
